import { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { ThreadListQuery } from '../../types';

interface ThreadFiltersProps {
  onFiltersChange: (filters: ThreadListQuery) => void;
}

export function ThreadFilters({ onFiltersChange }: ThreadFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<ThreadListQuery>({});

  const handleFilterChange = (key: keyof ThreadListQuery, value: string | undefined) => {
    const newFilters = { ...filters, [key]: value || undefined };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(filters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome ou telefone..."
          className="pl-10"
          value={filters.senderName || ''}
          onChange={(e) => handleFilterChange('senderName', e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleApplyFilters();
            }
          }}
        />
      </div>

      {/* Advanced filters toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={isOpen ? 'default' : 'outline'}
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filtros avançados
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-primary-foreground text-primary text-xs">
              {Object.values(filters).filter((v) => v !== undefined).length}
            </span>
          )}
        </Button>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={handleClearFilters} className="shrink-0">
            <X className="h-4 w-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      {/* Advanced filters panel */}
      {isOpen && (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
          {/* Status filter */}
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="open">Aberta</SelectItem>
                <SelectItem value="closed">Fechada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date filters */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data inicial</Label>
              <Input
                type="date"
                value={filters.fromDate || ''}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Data final</Label>
              <Input
                type="date"
                value={filters.toDate || ''}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
              />
            </div>
          </div>

          {/* Apply button */}
          <Button onClick={handleApplyFilters} className="w-full">
            Aplicar filtros
          </Button>
        </div>
      )}
    </div>
  );
}
